import { Mail, MailX } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useEmailPreferences, type EmailPreferences } from "@/hooks/use-email-preferences";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { hairlineClass, slateCardClass } from "@/lib/card-styles";
import { typeBodyClass, typeMetaClass } from "@/lib/typography";

interface PreferenceRow {
  key: Exclude<keyof EmailPreferences, "unsubscribedAll">;
  label: string;
  description: string;
}

/**
 * Which non-account emails Lyder Talk may send.
 *
 * This is one card, not three, because the three switches answer one question —
 * "what arrives in my inbox?" — and splitting them into separate surfaces made
 * the account pane look like it had three unrelated settings. Account mail is
 * called out once at the bottom: it is the one category a learner cannot turn
 * off, and saying so is more honest than hiding it.
 *
 * The data comes from the API, not from `profiles`: preferences live in the
 * user's metadata server-side, and the route resolves the user from their
 * claims. Nothing here passes a user id.
 */
export const EmailNotificationsCard = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { email, preferences, isLoading, error, update, pendingField } = useEmailPreferences();

  const rows: PreferenceRow[] = [
    {
      key: "groupActivity",
      label: t("settings.email.groupActivity", "Study group activity"),
      description: t(
        "settings.email.groupActivityDescription",
        "New shares, joins, topic picks and comments in your study groups",
      ),
    },
    {
      key: "reviewReminders",
      label: t("settings.email.reviewReminders", "Vocabulary review reminders"),
      description: t(
        "settings.email.reviewRemindersDescription",
        "A short note when words are due, so nothing slips past its review date",
      ),
    },
    {
      key: "weeklyDigest",
      label: t("settings.email.weeklyDigest", "Weekly progress digest"),
      description: t(
        "settings.email.weeklyDigestDescription",
        "One email each Monday with your sessions, words reviewed and what is due next",
      ),
    },
  ];

  const save = async (patch: Partial<EmailPreferences>) => {
    try {
      await update(patch);
    } catch (saveError) {
      toast({
        variant: "destructive",
        title: t("settings.email.saveError", "Could not save that preference"),
        description:
          saveError instanceof Error
            ? saveError.message
            : t("settings.email.saveErrorDescription", "Please try again in a moment."),
      });
    }
  };

  const allOff = preferences?.unsubscribedAll === true;

  return (
    <div className={cn(slateCardClass, "space-y-4 p-4 sm:p-5")}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            allOff
              ? "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400"
              : "bg-primary/10 text-primary",
          )}
        >
          {allOff ? (
            <MailX className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Mail className="h-4 w-4" aria-hidden="true" />
          )}
        </span>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-stone-900 dark:text-stone-50">
            {t("settings.email.title", "Email Notifications")}
          </h4>
          <p className={cn(typeBodyClass, "mt-0.5 text-xs")}>
            {email
              ? t("settings.email.description", "Sent to {{email}}", { email })
              : t("settings.email.descriptionFallback", "Choose what Lyder Talk sends to your inbox")}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <span className="sr-only">{t("settings.email.loading", "Loading email preferences…")}</span>
          {rows.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-4 py-1">
              <div className="space-y-1.5">
                <div className="h-3.5 w-40 animate-pulse rounded bg-stone-200/70 dark:bg-stone-700/60" />
                <div className="h-3 w-56 animate-pulse rounded bg-stone-200/50 dark:bg-stone-700/40" />
              </div>
              <div className="h-6 w-11 animate-pulse rounded-full bg-stone-200/70 dark:bg-stone-700/60" />
            </div>
          ))}
        </div>
      ) : error || !preferences ? (
        <p className="rounded-lg border border-amber-200/70 bg-amber-50/60 p-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300">
          {t(
            "settings.email.unavailable",
            "Email preferences are unavailable right now. Your current settings are unchanged.",
          )}
        </p>
      ) : allOff ? (
        <div className="space-y-3 rounded-xl border border-stone-200/80 bg-stone-50 p-3 dark:border-stone-800 dark:bg-stone-900/60">
          <div>
            <p className="text-sm font-medium text-stone-800 dark:text-stone-200">
              {t("settings.email.allOffTitle", "Notification emails are off")}
            </p>
            <p className="mt-0.5 text-xs text-stone-600 dark:text-stone-400">
              {t(
                "settings.email.allOffDescription",
                "You unsubscribed from every notification email. Account emails still arrive.",
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-xs"
            disabled={pendingField !== null}
            onClick={() =>
              save({
                unsubscribedAll: false,
                groupActivity: true,
                reviewReminders: true,
                weeklyDigest: true,
              })
            }
          >
            {t("settings.email.resubscribe", "Turn emails back on")}
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {rows.map((row, index) => {
            const checked = preferences[row.key] === true;
            return (
              <div
                key={row.key}
                className={cn(
                  "flex items-center justify-between gap-4 py-3",
                  index > 0 && ["border-t", hairlineClass],
                )}
              >
                <div className="min-w-0">
                  <Label htmlFor={`email-${row.key}`} className="text-sm font-medium">
                    {row.label}
                  </Label>
                  <p className={cn(typeBodyClass, "mt-0.5 text-xs")}>{row.description}</p>
                </div>
                <Switch
                  id={`email-${row.key}`}
                  checked={checked}
                  disabled={pendingField !== null}
                  aria-label={row.label}
                  onCheckedChange={(next) => void save({ [row.key]: next })}
                />
              </div>
            );
          })}
        </div>
      )}

      <p className={cn(typeMetaClass, "border-t pt-3", hairlineClass)}>
        {t(
          "settings.email.accountMailNote",
          "Password resets and email-change confirmations are account emails and are always sent.",
        )}
      </p>
    </div>
  );
};

export default EmailNotificationsCard;
